'use client';

import { forwardRef, ForwardedRef, useRef } from 'react';
import clsx from 'classnames';
import { VisuallyHidden } from '../../../helpers';

import styles from './Checkbox.module.scss';
import { CheckboxProps } from './types';

export const Checkbox = forwardRef(
	(
		{
			children,
			childrenClassName,
			labelClassName,
			disabled = false,
			position = 'start',
			spaceBetween = position === 'end',
			checked,
			value,
			onChange,
			onBlur,
			hasError,
			...props
		}: CheckboxProps,
		externalRef: ForwardedRef<HTMLInputElement>
	) => {
		const ref = useRef<HTMLInputElement | null>(null);

		const labelClasses = clsx(
			`Checkbox`,
			styles.checkboxLabel,
			{
				[styles['checkboxLabel--reversed']]: position === 'end',
				[styles['checkboxLabel--withSpacing']]: spaceBetween,
				[styles['checkboxLabel--disabled']]: disabled,
			},
			labelClassName
		);
		const inputClasses = clsx(styles.checkboxInput, {
			[styles['checkboxInput--checked']]: checked || ref?.current?.checked,
			[styles['checkboxInput--hasError']]: !!hasError,
			[styles['checkboxInput--disabled']]: disabled,
		});

		const childrenClassNames = clsx(
			styles.checkboxText,
			{
				[styles['checkboxText--hasError']]: !!hasError,
				[styles['checkboxText--disabled']]: disabled,
			},
			childrenClassName
		);

		return (
			<label className={labelClasses}>
				<VisuallyHidden>
					<input
						ref={(refObject) => {
							ref.current = refObject;

							if (typeof externalRef === 'function') externalRef(refObject);
							else if (externalRef !== null) externalRef.current = refObject;
						}}
						{...props}
						type="checkbox"
						value={value}
						checked={checked}
						disabled={disabled}
						onChange={onChange}
						onBlur={onBlur}
					/>
				</VisuallyHidden>
				<div className={inputClasses} />
				<span className={childrenClassNames}>{children}</span>
			</label>
		);
	}
);

Checkbox.displayName = 'Checkbox';
